// Name normalisation and token generation for guest-list lookup.
//
// This file is imported by BOTH the browser app and scripts/import-guests.mjs.
// The tokens written at import time have to hash to exactly the tokens the
// browser computes at search time, so keep this the single source of truth —
// changing it means re-running the import.

/**
 * Fold a name to a comparable form: strip accents, drop punctuation
 * (apostrophes, hyphens), collapse whitespace, lowercase.
 *   "  O'Brien-Smith, José " -> "o brien smith jose"
 */
export function normalize(input) {
  return String(input ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

/**
 * Every token a name should be findable by: the whole name with spaces
 * removed, plus each individual word. "Priya Raghavan" is therefore reachable
 * by "priyaraghavan", "priya" or "raghavan".
 *
 * Single letters are skipped so middle initials don't collect half the list.
 */
export function tokensForName(name) {
  const n = normalize(name);
  if (!n) return [];
  const words = n.split(" ");
  const tokens = new Set();
  tokens.add(words.join(""));
  for (const word of words) {
    if (word.length > 1) tokens.add(word);
  }
  return [...tokens];
}

/**
 * Tokens to probe for a search query, most specific first. The caller stops
 * at the first token that returns any match, so a full-name search never gets
 * diluted by everyone sharing the surname.
 */
export function tokensForQuery(query) {
  const n = normalize(query);
  if (!n) return [];
  const words = n.split(" ");
  const tokens = [];
  const seen = new Set();
  const push = (t) => {
    if (t.length > 1 && !seen.has(t)) {
      seen.add(t);
      tokens.push(t);
    }
  };
  push(words.join(""));
  // Surname first: it narrows far more than a first name does.
  for (let i = words.length - 1; i >= 0; i--) push(words[i]);
  return tokens;
}
